import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import isEqual from 'lodash.isequal';
import pick from 'lodash.pick';

import { AppState } from 'services/types';
import {
    VirtualizedTableColumnKey,
    VirtualizedTableColumnWidth,
    VirtualizedTableColumns,
    VirtualizedTableItem
} from 'services/virtualizedTable/types';
import { setColumnWidth } from 'services/columnWidths/actions';
import { VirtualizedTableValidateRow } from 'components/VirtualizedTable/types';
import VirtualizedTable from 'components/VirtualizedTable';
// @ts-ignore
import { selectCurrentSeasonId } from 'services/currentSeasonId/selectors';
import { selectIsAdmin, selectIsManager } from 'services/usersMe/selectors';
import { selectEquipmentKeyedById } from 'services/equipment/selectors';
import { selectEquipmentSetsKeyedById } from 'services/equipmentSets/selectors';
import {
    PostEquipmentSetPayload,
    PatchEquipmentSetPayload,
    Calibration,
    CalibrationPayload
} from 'services/equipmentSets/types';
import {
    requestedCreateEquipmentSet,
    requestedUpdateEquipmentSet,
    requestedDeleteEquipmentSet
} from 'services/equipmentSets/actions';
import { NozzleType } from 'services/equipmentSets/constants';

import {
    selectEquipmentSetsTableColumns,
    makeSelectEquipmentSetItems,
    selectWriteAccess
} from './selectors';

import { validateEquipmentSetRow } from './validators';

import './styles.scss';

interface OwnProps {
    tableId: string;
}

interface ConnectedProps {
    items: VirtualizedTableItem[];
    columns: VirtualizedTableColumns;
    isAdmin?: ReturnType<typeof selectIsAdmin>;
    isManager?: ReturnType<typeof selectIsManager>;
    seasonId: number;
    writeAccess: ReturnType<typeof selectWriteAccess>;
    equipment: ReturnType<typeof selectEquipmentKeyedById>;
    equipmentSets: ReturnType<typeof selectEquipmentSetsKeyedById>;
}

interface DispatchProps {
    setColumnWidth: typeof setColumnWidth;
    requestedCreateEquipmentSet: typeof requestedCreateEquipmentSet;
    requestedUpdateEquipmentSet: typeof requestedUpdateEquipmentSet;
    requestedDeleteEquipmentSet: typeof requestedDeleteEquipmentSet;
}

type Props = OwnProps & ConnectedProps & DispatchProps;

export class EquipmentSetsVirtualizedTable extends React.Component<Props> {
    validateRow: VirtualizedTableValidateRow = validateEquipmentSetRow;

    shouldComponentUpdate(nextProps: Props): boolean {
        return !isEqual(this.props, nextProps);
    }

    handleColumnResized = (
        columnKey: VirtualizedTableColumnKey,
        columnWidth: VirtualizedTableColumnWidth
    ): void => {
        const { tableId, ...props } = this.props;
        props.setColumnWidth(tableId, columnKey, columnWidth);
    };

    handleCreate = (item: VirtualizedTableItem): void => {
        const {
            seasonId,
            equipment,
            requestedCreateEquipmentSet: createEquipmentSet
        } = this.props;
        const { entityId, calibrations, ...eqSetPayload } = item;
        const payload = { ...eqSetPayload };
        const nozzleSet = equipment[eqSetPayload.nozzleSetId];
        const isWeedEqSet = nozzleSet.nozzleType === NozzleType.WEED_BOOM;

        if (entityId) {
            payload.entity = {
                id: entityId,
                seasonId
            };
        }

        if (calibrations) {
            payload.calibrations = calibrations.map(
                ({ id, ...calibration }: Calibration) => {
                    const subCalibration = isWeedEqSet
                        ? { fanSpeed: undefined }
                        : { swathWidth: undefined, nozzlesPerSide: undefined };
                    return {
                        ...calibration,
                        ...subCalibration
                    };
                }
            );
        }

        createEquipmentSet(payload as PostEquipmentSetPayload);
    };

    handleItemUpdate = ({
        id,
        entityId,
        calibrations
    }: VirtualizedTableItem): void => {
        const {
            seasonId,
            equipmentSets,
            equipment,
            requestedUpdateEquipmentSet: updateEquipmentSet
        } = this.props;
        const eqSet = equipmentSets[id];
        const nozzleSet = equipment[eqSet.nozzleSetId];
        const isWeedEqSet = nozzleSet.nozzleType === NozzleType.WEED_BOOM;
        const equipmentSet = {
            entity: {
                id: entityId,
                seasonId
            },
            calibrations: calibrations
                ? calibrations.map((calibration: CalibrationPayload) => {
                      const calibrationColumns = [
                          'rowSpacingMin',
                          'rowSpacingMax',
                          'gear',
                          'speed',
                          'flowRate',
                          'gaugePressure',
                          'pressureUnit',
                          'applicationRate',
                          'engineRpm'
                      ];
                      const calibrationPayload: Partial<CalibrationPayload> =
                          pick(calibration, calibrationColumns);

                      if (isWeedEqSet) {
                          calibrationPayload.swathWidth =
                              calibration.swathWidth;
                          if (calibration.nozzlesPerSide) {
                              calibrationPayload.nozzlesPerSide =
                                  calibration.nozzlesPerSide;
                          }
                      } else {
                          calibrationPayload.fanSpeed = calibration.fanSpeed;
                      }

                      return calibrationPayload;
                  })
                : []
        };

        if (id) {
            updateEquipmentSet(id, equipmentSet as PatchEquipmentSetPayload);
        }
    };

    handleDelete = (items: VirtualizedTableItem[]): void => {
        const { requestedDeleteEquipmentSet: deleteEquipmentSet } = this.props;

        if (items[0] && items[0].id) {
            deleteEquipmentSet(items[0].id);
        }
    };

    handleHiddenFormFields = (item: VirtualizedTableItem): string[] => {
        const { equipment } = this.props;
        let hiddenFormFields: string[] = [];

        if (item.nozzleSetId && equipment) {
            const nozzleSet = equipment[item.nozzleSetId];

            if (nozzleSet.nozzleType === NozzleType.WEED_BOOM) {
                hiddenFormFields = [
                    ...hiddenFormFields,
                    'calibrations.fanSpeed'
                ];
            } else {
                hiddenFormFields = [
                    ...hiddenFormFields,
                    'calibrations.swathWidth',
                    'calibrations.nozzlesPerSide'
                ];
            }
        } else {
            hiddenFormFields = [
                ...hiddenFormFields,
                'calibrations.fanSpeed',
                'calibrations.swathWidth',
                'calibrations.nozzlesPerSide'
            ];
        }

        return hiddenFormFields;
    };

    handleDisabledFormFields = (item: VirtualizedTableItem): string[] => {
        const disabledFormFields: string[] = [];

        if (!item.sprayerId || !item.tractorId || !item.nozzleSetId) {
            disabledFormFields.push('calibrations');
        }

        return disabledFormFields;
    };

    renderVirtualizedTable = ({ height, width }: Size): React.ReactElement => {
        const { tableId, items, columns, writeAccess } = this.props;

        return (
            <VirtualizedTable
                id={tableId}
                height={height}
                width={width}
                columns={columns}
                items={items}
                onColumnResized={this.handleColumnResized}
                label="Equipment Set"
                labelPlural="Equipment Sets"
                enableCreating={writeAccess}
                enableDeleting={writeAccess}
                enableItemEditing={writeAccess}
                onCreate={this.handleCreate}
                onDelete={this.handleDelete}
                onItemUpdate={this.handleItemUpdate}
                modalSize="fullscreen"
                validateRow={this.validateRow}
                selectable
                hiddenFormFields={this.handleHiddenFormFields}
                disabledFormFields={this.handleDisabledFormFields}
            />
        );
    };

    render(): React.ReactElement {
        return (
            <AutoSizer>
                {(size: Size): React.ReactElement =>
                    this.renderVirtualizedTable(size)
                }
            </AutoSizer>
        );
    }
}

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    items: makeSelectEquipmentSetItems(),
    columns: selectEquipmentSetsTableColumns,
    seasonId: selectCurrentSeasonId,
    writeAccess: selectWriteAccess,
    equipment: selectEquipmentKeyedById,
    equipmentSets: selectEquipmentSetsKeyedById
});

export default connect(mapStateToProps, {
    setColumnWidth,
    requestedCreateEquipmentSet,
    requestedUpdateEquipmentSet,
    requestedDeleteEquipmentSet
})(EquipmentSetsVirtualizedTable);
